/**
 * Task Management System - API Client
 */
import axios from '@/api/client'

// ==================== Task Types ====================

export const taskTypesApi = {
  list(params = {}) {
    return axios.get('/task-types/', { params })
  },
  create(data) {
    return axios.post('/task-types/create/', data)
  },
  update(id, data) {
    return axios.patch(`/task-types/${id}/`, data)
  },
  delete(id) {
    return axios.delete(`/task-types/${id}/delete/`)
  },
}

// ==================== Operational Tasks ====================

export const tasksOpsApi = {
  create(data) {
    return axios.post('/tasks/', data)
  },
  myTasks(params = {}) {
    return axios.get('/tasks/my/', { params })
  },
  teamTasks(teamId, params = {}) {
    return axios.get(`/tasks/team/${teamId}/`, { params })
  },
  journal(params = {}) {
    return axios.get('/tasks/journal/', { params })
  },
  claim(taskId) {
    return axios.post(`/tasks/${taskId}/claim/`)
  },
  release(taskId) {
    return axios.post(`/tasks/${taskId}/release/`)
  },
  complete(taskId, data = {}) {
    return axios.post(`/tasks/${taskId}/complete/`, data)
  },
  // { task_ids: [...] } → { completed: [ids], failed: [{ id, error }] }
  bulkComplete(taskIds, data = {}) {
    return axios.post('/tasks/bulk-complete/', { ...data, task_ids: taskIds })
  },
  take(taskId) {
    return axios.post(`/tasks/${taskId}/take/`)
  },
  // Move / resize a slot task (start/end ISO). Owner can edit own time;
  // reassignment (assignee_employee_id / assignee_user_id) is admin-only.
  reschedule(taskId, data) {
    return axios.patch(`/tasks/${taskId}/reschedule/`, data)
  },
  // Change status: todo | in_progress | done | cancelled.
  setStatus(taskId, status) {
    return axios.patch(`/tasks/${taskId}/status/`, { status })
  },
  addComment(taskId, data) {
    return axios.post(`/tasks/${taskId}/comment/`, data)
  },
  getActivity(taskId) {
    return axios.get(`/tasks/${taskId}/activity/`)
  },
  getComments(taskId) {
    return axios.get(`/tasks/${taskId}/comments/`)
  },
  analytics() {
    return axios.get('/tasks/analytics/')
  },
  teamDashboard() {
    return axios.get('/tasks/team-dashboard/')
  },
  byEntity(entityType, entityId) {
    return axios.get('/tasks/by-entity/', { params: { entity_type: entityType, entity_id: entityId } })
  },
  getPrefs() {
    return axios.get('/tasks/prefs/')
  },
  savePrefs(prefs) {
    return axios.put('/tasks/prefs/', prefs)
  },
}

// ==================== User Presence ====================

export const presenceApi = {
  startWorkday() {
    return axios.post('/presence/start-workday/')
  },
  endWorkday() {
    return axios.post('/presence/end-workday/')
  },
  updateStatus(data) {
    return axios.post('/presence/status/', data)
  },
  myPresence() {
    return axios.get('/presence/my/')
  },
  teamPresence(teamId) {
    return axios.get(`/presence/team/${teamId}/`)
  },
  available() {
    return axios.get('/presence/available/')
  },
  heartbeat() {
    return axios.post('/presence/heartbeat/')
  },
  canAssign(userId) {
    return axios.get(`/presence/can-assign/${userId}/`)
  },
  history(params = {}) {
    return axios.get('/presence/history/', { params })
  },
  workdaySummary(params = {}) {
    return axios.get('/presence/workday-summary/', { params })
  },
}

// ==================== Task Notifications ====================

export const taskNotificationsApi = {
  list(params = {}) {
    return axios.get('/task-notifications/', { params })
  },
  unreadCount() {
    return axios.get('/task-notifications/unread-count/')
  },
  markRead(id) {
    return axios.post(`/task-notifications/${id}/read/`)
  },
  markAllRead() {
    return axios.post('/task-notifications/read-all/')
  },
  markReadByTask(taskId) {
    return axios.post(`/task-notifications/read-by-task/${taskId}/`)
  },
}

// ==================== Quality Issues ====================

export const qualityApi = {
  listIssues(params = {}) {
    return axios.get('/quality/issues/', { params })
  },
  issueDetail(id) {
    return axios.get(`/quality/issues/${id}/`)
  },
  acknowledge(id) {
    return axios.post(`/quality/issues/${id}/acknowledge/`)
  },
  resolve(id, data = {}) {
    return axios.post(`/quality/issues/${id}/resolve/`, data)
  },
  dismiss(id, data = {}) {
    return axios.post(`/quality/issues/${id}/dismiss/`, data)
  },
  dashboard() {
    return axios.get('/quality/dashboard/')
  },
}

// ==================== Running Late ====================

export const runningLateApi = {
  report(data) {
    return axios.post('/running-late/', data)
  },
}
